import { Service } from '@/services/api';
import { ReviewEditInput } from '@/types/ai';
import {
  BinderBatchResult,
  ConfirmBinderResponse,
  ExternalPolicyBinderSubmission,
  MAX_BINDER_SIZE_BYTES,
  MAX_CONCURRENT_BINDER_UPLOADS,
} from '@/types/ExternalPolicyBinder';

export function binderFileProblem(file: File): string | null {
  if (!file.name.toLowerCase().endsWith('.pdf')) {
    return 'Only PDF files are supported for binder scanning.';
  }
  if (file.size > MAX_BINDER_SIZE_BYTES) {
    return 'Binder files must be 25 MB or smaller.';
  }
  return null;
}

class ExternalPolicyBinderService extends Service {
  // The prompt version decides which fields come out of the binder, so the submitter
  // names it rather than the server picking whichever was published last.
  async submitBinder(
    accountId: string | number,
    file: File,
    promptVersionId: number,
  ): Promise<ExternalPolicyBinderSubmission> {
    return await this.postFile<ExternalPolicyBinderSubmission>(
      `/v2/accounts/${accountId}/external-policy-binders`,
      file,
      { promptVersionId: String(promptVersionId) },
    ).then(this.handleResponse);
  }

  // Results stay in the order the files were given, however they interleave on the wire.
  async submitBinders(
    accountId: string | number,
    files: File[],
    promptVersionId: number,
  ): Promise<BinderBatchResult[]> {
    const results: BinderBatchResult[] = new Array(files.length);
    let next = 0;
    const uploadUntilDone = async () => {
      while (next < files.length) {
        const index = next++;
        results[index] = await this.submitOneBinder(accountId, files[index], promptVersionId);
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(MAX_CONCURRENT_BINDER_UPLOADS, files.length) }, uploadUntilDone),
    );
    return results;
  }

  private async submitOneBinder(
    accountId: string | number,
    file: File,
    promptVersionId: number,
  ): Promise<BinderBatchResult> {
    const problem = binderFileProblem(file);
    if (problem) {
      return { file, outcome: 'rejected', reason: problem };
    }
    try {
      const submission = await this.submitBinder(accountId, file, promptVersionId);
      return { file, outcome: 'queued', submission };
    } catch (err) {
      const cause = (err as { cause?: { status?: number; data?: { existingSubmissionId?: number } } })?.cause;
      if (cause?.status === 409) {
        return { file, outcome: 'duplicate', existingSubmissionId: cause.data?.existingSubmissionId ?? null };
      }
      // Only a 4xx message is meant for the broker; a 5xx statusText isn't. A 4xx is also the
      // file's own fault, so only the rest are worth offering a retry for.
      const status = cause?.status ?? 0;
      if (status >= 400 && status < 500) {
        return {
          file,
          outcome: 'rejected',
          reason: err instanceof Error ? err.message : 'Upload failed. Please try again.',
        };
      }
      return { file, outcome: 'rejected', reason: 'Upload failed. Please try again.', retryable: true };
    }
  }

  async listSubmissions(accountId: string | number, activeOnly = false): Promise<ExternalPolicyBinderSubmission[]> {
    return await this.get<ExternalPolicyBinderSubmission[]>(
      `/v2/accounts/${accountId}/external-policy-binders`,
      activeOnly ? { active: 'true' } : undefined,
    ).then(this.handleResponse);
  }

  async getSubmission(accountId: string | number, submissionId: number): Promise<ExternalPolicyBinderSubmission> {
    return await this.get<ExternalPolicyBinderSubmission>(
      `/v2/accounts/${accountId}/external-policy-binders/${submissionId}`,
    ).then(this.handleResponse);
  }

  // The policy records are built server-side from the extraction; only the reviewer's
  // in-flight edits travel with the confirm. Edits already saved on the review screen are
  // folded in by the server, so an untouched confirm sends none.
  async confirmSubmission(
    accountId: string | number,
    submissionId: number,
    edits: ReviewEditInput[] = [],
  ): Promise<ConfirmBinderResponse> {
    return await this.post<ConfirmBinderResponse>(
      `/v2/accounts/${accountId}/external-policy-binders/${submissionId}/confirm`,
      { edits },
    ).then(this.handleResponse);
  }

  async discardSubmission(accountId: string | number, submissionId: number, reason?: string): Promise<void> {
    await this.post<void>(`/v2/accounts/${accountId}/external-policy-binders/${submissionId}/discard`, {
      reason: reason ?? '',
    }).then(this.handleResponse);
  }
}

export const externalPolicyBinderService = new ExternalPolicyBinderService();
